if (!customElements.get("pdp-swatch-picker")) {
  customElements.define("pdp-swatch-picker", class extends HTMLElement {
    initialize(row) {
      this.row = row;
      this.drawer = this.closest("drawer-section");
      this.configurator = row.closest("product-configurator");
      this.modal = this.querySelector("dialog");
      this.groups = new Map();
      this.cards = [];
      const title = this.querySelector("[data-picker-title]");
      title.id = `${this.drawer.id}-title`;
      this.drawer.setAttribute("aria-labelledby", title.id);
      this.buildCards();

      this.querySelectorAll("[data-picker-view]").forEach((button) => {
        button.addEventListener("click", () => {
          this.dataset.view = button.dataset.pickerView;
          this.querySelectorAll("[data-picker-view]").forEach((item) => {
            item.setAttribute("aria-pressed", String(item === button));
          });
        });
      });

      this.modal.querySelector("button").addEventListener("click", () => this.modal.close());
      this.modal.addEventListener("click", (event) => {
        if (event.target === this.modal) this.modal.close();
      });
      this.modal.addEventListener("keydown", (event) => {
        if (event.key === "Escape") event.stopPropagation();
      });
      this.modal.addEventListener("close", () => this.zoomTrigger?.focus());
      const image = this.modal.querySelector("img");
      image.addEventListener("load", () => this.setImageState("ready"));
      image.addEventListener("error", () => this.setImageState("error"));

      this.onDrawerClosed = (event) => {
        if (event.detail.id === this.drawer.id) {
          if (this.modal.open) this.modal.close();
          this.row.toggle.setAttribute("aria-expanded", "false");
          this.row.toggle.focus();
        } else if (event.detail.id === "drawer-fabric-details" && this.detailsOpen) {
          this.detailsOpen = false;
          this.inert = false;
          this.infoTrigger?.focus();
        }
      };
      this.onDrawerOpen = (event) => {
        if (event.detail.id !== "drawer-fabric-details" || !this.drawer.classList.contains("drawer--opened")) return;
        this.infoTrigger = document.activeElement;
        this.detailsOpen = true;
        this.inert = true;
        requestAnimationFrame(() => document.querySelector("#drawer-fabric-details [data-drawer-close]")?.focus());
      };
      this.onKeydown = (event) => this.handleKeydown(event);
      window.addEventListener("drawerClosed", this.onDrawerClosed);
      window.addEventListener("drawerOpen", this.onDrawerOpen);
      document.addEventListener("keydown", this.onKeydown, true);
      this.inputObserver = new MutationObserver(() => this.sync());
      row.swatches.forEach((swatch) => this.inputObserver.observe(swatch.querySelector("input"), {
        attributes: true, attributeFilter: ["disabled", "checked"],
      }));
      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener("drawerClosed", this.onDrawerClosed);
      window.removeEventListener("drawerOpen", this.onDrawerOpen);
      document.removeEventListener("keydown", this.onKeydown, true);
      this.inputObserver?.disconnect();
      if (this.drawer?.classList.contains("drawer--opened")) this.drawer.close();
    }

    buildCards() {
      const groupTemplate = this.querySelector("[data-picker-group-template]");
      const cardTemplate = this.querySelector("[data-picker-card-template]");
      this.row.swatches.forEach((swatch) => {
        const input = swatch.querySelector("input");
        const data = swatch.dataset;
        const name = swatch.title || input.value;
        const groupKey = data.swatchGroup || "options";
        if (!this.groups.has(groupKey)) {
          const group = groupTemplate.content.firstElementChild.cloneNode(true);
          const label = data.swatchGroupLabel || this.querySelector("[data-picker-title]").textContent;
          group.querySelector("h3").textContent = label;
          group.querySelector("[role=radiogroup]").setAttribute("aria-label", label);
          const description = group.querySelector(".swatch-picker__description");
          description.textContent = data.swatchDescription || "";
          description.hidden = !data.swatchDescription;
          this.querySelector("[data-picker-groups]").append(group);
          this.groups.set(groupKey, { element: group });
        }
        const group = this.groups.get(groupKey);
        const card = cardTemplate.content.firstElementChild.cloneNode(true);
        const choice = card.querySelector(".swatch-picker__choice");
        card.querySelector(".swatch-picker__name").textContent = name;
        const media = card.querySelector(".swatch-picker__media");
        const sourceImage = swatch.querySelector("img");
        const src = data.swatchImage || sourceImage?.src;
        if (src) {
          const image = document.createElement("img");
          image.src = src;
          image.alt = "";
          image.width = 520;
          image.height = 500;
          image.loading = "lazy";
          media.append(image);
        } else {
          const fill = swatch.querySelector(".pdp-fabric-swatch__media span");
          if (fill) media.style.background = fill.style.background;
        }
        choice.addEventListener("click", () => {
          if (input.disabled) return;
          input.click();
          this.sync();
          this.drawer.close();
        });
        choice.addEventListener("keydown", (event) => {
          if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          const choices = [...group.element.querySelectorAll(".swatch-picker__choice:not(:disabled)")];
          const index = choices.indexOf(choice);
          const next = event.key === "Home" ? 0 : event.key === "End" ? choices.length - 1
            : (index + (["ArrowLeft", "ArrowUp"].includes(event.key) ? -1 : 1) + choices.length) % choices.length;
          choices[next]?.focus();
          choices[next]?.click();
        });
        const zoom = card.querySelector(".swatch-picker__zoom");
        zoom.hidden = !src;
        zoom.setAttribute("aria-label", `${this.dataset.zoomLabel}: ${name}`);
        zoom.addEventListener("click", () => this.openZoom(zoom, data.swatchZoom || src, name));
        const info = card.querySelector(".swatch-picker__info");
        info.hidden = !data.swatchHandle;
        info.dataset.handle = data.swatchHandle || "";
        info.setAttribute("aria-label", `${this.dataset.infoLabel}: ${name}`);
        group.element.querySelector(".swatch-picker__grid").append(card);
        this.cards.push({ input, card, choice, group, family: data.swatchFamily });
      });
    }

    sync() {
      const differences = this.configurator?.getFabricFamilyPriceDiffs?.();
      this.cards.forEach(({ input, card, choice, family }) => {
        choice.setAttribute("aria-checked", String(input.checked));
        choice.disabled = input.disabled;
        card.classList.toggle("is-selected", input.checked);
        const price = card.querySelector("[data-picker-price]");
        const difference = differences?.get(family);
        price.hidden = !Number.isFinite(difference);
        price.textContent = price.hidden ? "" : this.configurator.formatPriceDifference(difference);
      });
      this.groups.forEach(({ element }) => {
        const choices = [...element.querySelectorAll(".swatch-picker__choice:not(:disabled)")];
        const active = choices.find((choice) => choice.getAttribute("aria-checked") === "true") || choices[0];
        element.querySelectorAll(".swatch-picker__choice").forEach((choice) => { choice.tabIndex = choice === active ? 0 : -1; });
      });
    }

    open() {
      this.sync();
      this.inert = false;
      this.row.toggle.setAttribute("aria-expanded", "true");
      window.dispatchEvent(new CustomEvent("drawerOpen", { detail: { id: this.drawer.id } }));
      requestAnimationFrame(() => this.querySelector("[data-drawer-close]").focus());
    }

    setImageState(state) {
      this.modal.querySelector("img").hidden = state !== "ready";
      this.modal.querySelector("[data-picker-loader]").hidden = state !== "loading";
      this.modal.querySelector("[data-picker-image-error]").hidden = state !== "error";
    }

    openZoom(trigger, src, name) {
      this.zoomTrigger = trigger;
      const image = this.modal.querySelector("img");
      this.setImageState("loading");
      image.alt = name;
      image.src = src;
      if (image.complete && image.naturalWidth) this.setImageState("ready");
      this.modal.showModal();
    }

    handleKeydown(event) {
      if (!this.drawer.classList.contains("drawer--opened") || this.modal.open) return;
      const activeDrawer = this.detailsOpen ? document.getElementById("drawer-fabric-details") : this.drawer;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        activeDrawer?.close();
      } else if (event.key === "Tab") {
        const focusable = [...activeDrawer.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]')]
          .filter((element) => element.getClientRects().length && !element.closest("[hidden]"));
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && (document.activeElement === first || !activeDrawer.contains(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !activeDrawer.contains(document.activeElement))) {
          event.preventDefault(); first?.focus();
        }
      }
    }
  });
}
